import { Box, CircularProgress, CircularProgressProps, circularProgressClasses } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import styles from "./spinner.module.css";
import Lottie from "lottie-react";
import loadingDashCircle from "./loadingDashCircle.json";

/** The color of the component. */
export type CoreSpinnerColors = "primary" | "secondary" | "red" | "green" | "yellow" | "blue" | "orange";

type CoreSpinnerProps = {
	/** The color of the component. @default "primary" */
	color?: CoreSpinnerColors;
	/** The size of the component in px. @default 40 */
	size?: number | string;
	/** When `true`, displays a checkmark in the center. @default false */
	showCheck?: boolean;
	version?: "v1" | "v2"; // CHANGE
} & Omit<CircularProgressProps, "color" | "size">;

export function CoreSpinner({ color, size, showCheck, version = "v1", ...props }: CoreSpinnerProps) {
	const colorClass = color ? styles[`spinner--${color}`] : styles["spinner--primary"];

	const circleSize = size ?? 40;
	const thickness = 2.8;
	const checkSize = typeof circleSize === "string" ? `calc(${circleSize} * 0.8)` : circleSize * 0.8;

	return version === "v1" ? (
		<div className={`CoreSpinner-root ${styles["core-spinner"]}`}>
			<CircularProgress
				variant="determinate"
				sx={{
					position: "relative",
					color: "#e5e8eC",
				}}
				size={circleSize}
				thickness={thickness}
				value={100}
			/>
			<CircularProgress
				{...props}
				className={`${colorClass} ${props.className ?? ""}`}
				sx={{
					position: "absolute",
					left: 0,
					[`& .${circularProgressClasses.circle}`]: {
						strokeLinecap: "round",
					},
				}}
				size={circleSize}
				thickness={thickness}
			/>

			<div
				style={{
					top: 0,
					left: 0,
					bottom: 0,
					right: 0,
					position: "absolute",
					display: showCheck ? "flex" : "none",
					alignItems: "center",
					justifyContent: "center",
					width: circleSize,
					height: circleSize,
				}}
			>
				<CheckRoundedIcon className={colorClass} style={{ margin: "0 0 -1 -1", fontSize: checkSize }} />
			</div>
		</div>
	) : (
		<Box width={size ?? "40px"} height={size ?? "40px"} role="status" aria-live="polite" aria-busy="true">
			<Lottie animationData={loadingDashCircle} />
		</Box>
	);
}
